// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import GPAChart from "../ProgressionReportSection/ProgressionGPACharts/GPAChart/GPAChart";
import ProgressionBaselineButton, {
  BaselineActionType,
} from "../ProgressionBaselineButton/ProgressionBaselineButton";

// Utils
import { getVisualField } from "src/utils/exams/examReportsHelper";
import { getLocalDateTimeWithZone } from "src/utils/date/dateHelper";
import {
  getExternalExamStats,
  isBeforeBaselineDay,
  PROGRESSION_STAT_KEYS,
} from "src/utils/exams/progressionAnalysisHelper";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Types
import {
  EnrichedExternalExamType,
  GPAExclusionReason,
  GPAVisitType,
} from "src/services/exams/types";

const CHART_COLUMNS = [
  { chartKind: "Grayscale", title: "Greyscale" },
  {
    chartKind: "TotalDeviationProbability",
    title: "Total Deviation Percentile",
  },
  {
    chartKind: "PatternDeviationProbability",
    title: "Pattern Deviation Percentile",
  },
] as const;

type ProgressionExternalExamRowType = {
  externalExam: EnrichedExternalExamType;
  gpaVisit?: GPAVisitType;
  gpaExclusion?: GPAExclusionReason;
  // completion date of the analysis's oldest baseline exam
  earliestBaselineDate?: string | null;
  // Which baseline button the row's header shows, if any
  baselineAction?: BaselineActionType;
  onBaselineClick?: () => void;
};

const ProgressionExternalExamRow = ({
  externalExam,
  gpaVisit,
  gpaExclusion,
  earliestBaselineDate,
  baselineAction,
  onBaselineClick,
}: ProgressionExternalExamRowType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();

  const examData = externalExam?.data;

  const localDateAndTimeWithZone = useMemo(
    () =>
      examData?.completionDate
        ? getLocalDateTimeWithZone(
            examData.completionDate,
            clinicInfo?.timezone
          )
        : { date: "N/A", time: "N/A" },
    [examData?.completionDate, clinicInfo?.timezone]
  );

  const chartsByKind = useMemo(() => {
    const byKind: Record<string, string> = {};
    externalExam?.charts?.forEach((chart) => {
      byKind[chart.chartKind] = chart.imageUrl;
    });
    return byKind;
  }, [externalExam?.charts]);

  const hasCharts = CHART_COLUMNS.some(
    (column) => chartsByKind[column.chartKind]
  );

  // Before the earliest baseline day, GPA leaves the exam out entirely
  const isBeforeBaseline = useMemo(
    () =>
      isBeforeBaselineDay(
        examData?.completionDate,
        earliestBaselineDate,
        clinicInfo?.timezone ?? null
      ),
    [examData?.completionDate, earliestBaselineDate, clinicInfo?.timezone]
  );

  const showsGpaColumn =
    Boolean(gpaVisit) || Boolean(gpaExclusion) || isBeforeBaseline;

  const chartSize = showsGpaColumn ? 200 : 260;

  const columns = useMemo(
    () => [
      {
        title: t("Algorithm"),
        value: examData?.algorithmType
          ? t(examData.algorithmType)
          : examData?.algorithmTypeReadable || "N/A",
        isBlueText: true,
      },
      {
        title: t("Grid Type"),
        value: getVisualField(examData?.gridType) || "N/A",
        isBlueText: true,
      },
      {
        title: t("Source"),
        value: externalExam?.sourcePlatform ?? "N/A",
      },
      {
        title: t("Date"),
        value: localDateAndTimeWithZone.date,
      },
      {
        title: t("Time_hour"),
        value: localDateAndTimeWithZone.time,
      },
      {
        title: t("Eye"),
        value: externalExam?.eye ? t(externalExam.eye) : "N/A",
      },
    ],
    [
      examData?.algorithmType,
      examData?.algorithmTypeReadable,
      examData?.gridType,
      externalExam?.sourcePlatform,
      externalExam?.eye,
      localDateAndTimeWithZone,
      t,
    ]
  );

  const stats = useMemo(() => {
    const examStats = getExternalExamStats(examData);
    return PROGRESSION_STAT_KEYS.map((key) => ({
      title: t(key),
      value: examStats[key],
    }));
  }, [examData, t]);

  return (
    <Grid
      container
      className="retinalogik-report"
      data-testid={`external-exam-report-${externalExam?.id}`}
      id={`external-exam-report-${externalExam?.id}`}
    >
      <Grid
        container
        item
        xs={12}
        display="flex"
        alignItems="flex-start"
        border="1px solid #000"
        borderRadius="4px 4px 0px 0px"
        padding="8px"
        gap="8px"
      >
        <Grid
          item
          xs
          display="grid"
          gridTemplateColumns={{
            xs: "repeat(2, 1fr)",
            sm: "repeat(3, 1fr)",
          }}
          columnGap="8px"
        >
          {columns.map((column) => (
            <Typography key={column.title} fontSize="12px">
              <span style={{ fontWeight: 600 }}>{column.title}:</span>{" "}
              <span
                style={{
                  color: Boolean(column.isBlueText) ? "#2563EB" : "#000",
                }}
              >
                {column.value}
              </span>
            </Typography>
          ))}
        </Grid>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: "4px",
          }}
        >
          <Typography
            data-testid={`external-exam-stamp-${externalExam?.id}`}
            fontSize="12px"
            fontWeight={600}
            color="#2563EB"
            border="1px solid #2563EB"
            borderRadius="4px"
            padding="2px 8px"
            whiteSpace="nowrap"
          >
            {t("External Exam")}
          </Typography>

          {/* Hidden without a baseline action, so the columns keep the same width as the
              baseline rows' */}
          {onBaselineClick && (
            <ProgressionBaselineButton
              action={baselineAction ?? "update"}
              onClick={onBaselineClick}
              isHidden={!baselineAction}
              isExternal
            />
          )}
        </Box>
      </Grid>

      {hasCharts ? (
        <Grid
          container
          item
          xs={12}
          sx={{
            display: "flex",
            flexDirection: "column",
            borderLeft: "1px solid #000",
            borderRight: "1px solid #000",
            padding: "8px",
            rowGap: "20px",
          }}
        >
          <Grid
            container
            justifyContent="space-around"
            flexWrap="nowrap"
            columnGap="16px"
            rowGap="20px"
            sx={{ overflowX: "auto" }}
          >
            {CHART_COLUMNS.map((column) => (
              <Grid
                key={column.chartKind}
                item
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Typography fontSize="12px" textAlign="center">
                  {t(column.title)}
                </Typography>
                {chartsByKind[column.chartKind] ? (
                  <Box
                    component="img"
                    src={chartsByKind[column.chartKind]}
                    alt={t(column.title)}
                    data-testid={`external-chart-${column.chartKind}-${externalExam?.id}`}
                    sx={{
                      width: `${chartSize}px`,
                      height: "auto",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <Typography
                    className="retinalogik-analysis-report"
                    fontSize="12px"
                    textAlign="center"
                    sx={{ width: `${chartSize}px` }}
                  >
                    {t("Not printed on this exam.")}
                  </Typography>
                )}
              </Grid>
            ))}

            {showsGpaColumn && (
              <Grid
                item
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Typography fontSize="12px" textAlign="center">
                  {t("GPA")}
                </Typography>
                {gpaVisit ? (
                  <GPAChart
                    gpaPoints={gpaVisit.gpa_chart.points}
                    containerSize={200}
                  />
                ) : (
                  <Typography
                    className="retinalogik-analysis-report"
                    fontSize="12px"
                    textAlign="center"
                    sx={{ width: "200px" }}
                  >
                    {isBeforeBaseline
                      ? t(
                          "Not charted — exams before the selected GPA baselines are excluded from progression analysis."
                        )
                      : gpaExclusion === "baseline"
                        ? t(
                            "Baseline exam — GPA compares later exams against it."
                          )
                        : t(
                            "Not charted — another exam from the same day was used for GPA."
                          )}
                  </Typography>
                )}
              </Grid>
            )}
          </Grid>
        </Grid>
      ) : (
        <Grid
          container
          item
          xs={12}
          display="flex"
          borderLeft="1px solid #000"
          borderRight="1px solid #000"
          padding="12px 40px"
        >
          <Typography
            className="retinalogik-analysis-report"
            fontSize="12px"
            textAlign="center"
            width="100%"
          >
            {t("No charts could be read from this exam's printout.")}
          </Typography>
        </Grid>
      )}

      <Grid
        container
        item
        xs={12}
        display="flex"
        border="1px solid black"
        borderRadius="0px 0px 4px 4px"
        padding="8px"
      >
        <Grid container item xs={12} rowGap="4px">
          {stats.map((stat) => (
            <Grid item xs={4} key={stat.title}>
              {stat.value !== null && (
                <Typography
                  data-testid={`external-${stat.title.toLowerCase()}-${
                    externalExam?.id
                  }`}
                  fontSize="12px"
                >
                  <span style={{ fontWeight: 600 }}>{stat.title}: </span>
                  {stat.value}
                </Typography>
              )}
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ProgressionExternalExamRow;
